import React, { useEffect, useState } from "react";
import { Button, Stack } from "@mui/material";
import DateInput from "../input/DateInput";
import FilterBox from "./FilterBox";
import moment from "moment";
import TimeOptions from "../input/TimeOptions";

type TimeType = {
  timeFrom: string | undefined;
  timeTo: string | undefined;
};
interface IFilterTimeFormTo {
  title?: string;
  defaultTimeFrom?: string;
  defaultTimeTo?: string;
  showOptions?: boolean;
  onSearch: (params: TimeType) => void;
  onOptionChange?: (op: any) => void;
}

const grids = [
  {
    title: "Theo ngày",
    options: [
      {
        title: "Hôm nay",
        value: "today",
      },
      {
        title: "Hôm qua",
        value: "yesterday",
      },
    ],
  },
  {
    title: "Theo tuần",
    options: [
      {
        title: "Tuần này",
        value: "thisweek",
      },
      {
        title: "Tuần trước",
        value: "lastweek",
      },
      {
        title: "7 ngày qua",
        value: "7daysago",
      },
    ],
  },
  {
    title: "Theo tháng",
    options: [
      {
        title: "Tháng này",
        value: "thismonth",
      },
      {
        title: "Tháng trước",
        value: "lastmonth",
      },
      {
        title: "30 ngày qua",
        value: "30daysago",
      },
    ],
  },
  {
    title: "Theo quý",
    options: [
      {
        title: "Quý này",
        value: "thisquarter",
      },
      {
        title: "Quý trước",
        value: "lastquarter",
      },
    ],
  },
  {
    title: "Theo năm",
    options: [
      {
        title: "Năm nay",
        value: "thisyear",
      },
      {
        title: "Năm trước",
        value: "lastyear",
      },
    ],
  },
];

function FilterTimeFromTo({
  title,
  defaultTimeFrom,
  defaultTimeTo,
  showOptions,
  onSearch,
  onOptionChange,
}: IFilterTimeFormTo) {
  const [active, setActive] = useState<boolean>(false);
  const [option, setOption] = useState<
    { title: string; value: string } | undefined
  >();
  const [time, setTime] = useState<TimeType>({
    timeFrom: defaultTimeFrom,
    timeTo: defaultTimeTo,
  });

  const handleTimeChange = ({ key, value }: { key: string; value: Date }) => {
    if (option) {
      setOption(undefined);
    }
    setTime({ ...time, [key]: value });
  };

  const handleFilterTime = (
    time: TimeType,
    currentOption?: { title: string; value: string }
  ) => {
    onSearch(time);
    onOptionChange?.(currentOption);
    setActive(!!time.timeFrom || !!time.timeTo);
  };

  useEffect(() => {
    if (option) {
      moment.locale("vi");
      moment.updateLocale("vi", {
        week: {
          dow: 1,
        },
      });
      let timeFrom: string;
      let timeTo: string;
      switch (option.value) {
        case "today":
          timeFrom = timeTo = moment().format("YYYY-MM-DD");
          break;
        case "yesterday":
          timeFrom = timeTo = moment().subtract(1, "days").format("YYYY-MM-DD");
          break;
        case "thisweek":
          timeFrom = moment().startOf("weeks").format("YYYY-MM-DD");
          timeTo = moment().format("YYYY-MM-DD");
          break;
        case "lastweek":
          timeFrom = moment()
            .subtract(1, "weeks")
            .startOf("weeks")
            .format("YYYY-MM-DD");
          timeTo = moment()
            .subtract(1, "weeks")
            .endOf("weeks")
            .format("YYYY-MM-DD");
          break;
        case "7daysago":
          timeFrom = moment().subtract(7, "days").format("YYYY-MM-DD");
          timeTo = moment().subtract(1, "days").format("YYYY-MM-DD");
          break;
        case "thismonth":
          timeFrom = moment().startOf("months").format("YYYY-MM-DD");
          timeTo = moment().format("YYYY-MM-DD");
          break;
        case "lastmonth":
          timeFrom = moment()
            .subtract(1, "months")
            .startOf("months")
            .format("YYYY-MM-DD");
          timeTo = moment()
            .subtract(1, "months")
            .endOf("months")
            .format("YYYY-MM-DD");
          break;
        case "30daysago":
          timeFrom = moment().subtract(30, "days").format("YYYY-MM-DD");
          timeTo = moment().subtract(1, "days").format("YYYY-MM-DD");
          break;
        case "thisquarter":
          timeFrom = moment().startOf("quarters").format("YYYY-MM-DD");
          timeTo = moment().format("YYYY-MM-DD");
          break;
        case "lastquarter":
          timeFrom = moment()
            .subtract(1, "quarters")
            .startOf("quarters")
            .format("YYYY-MM-DD");
          timeTo = moment()
            .subtract(1, "quarters")
            .endOf("quarters")
            .format("YYYY-MM-DD");
          break;
        case "thisyear":
          timeFrom = moment().startOf("years").format("YYYY-MM-DD");
          timeTo = moment().format("YYYY-MM-DD");
          break;
        case "lastyear":
          timeFrom = moment()
            .subtract(1, "years")
            .startOf("years")
            .format("YYYY-MM-DD");
          timeTo = moment()
            .subtract(1, "years")
            .endOf("years")
            .format("YYYY-MM-DD");
          break;
        default:
          timeFrom = moment().startOf("months").format("YYYY-MM-DD");
          timeTo = moment().format("YYYY-MM-DD");
          break;
      }
      setTime({ timeFrom, timeTo });
      handleFilterTime({ timeFrom, timeTo }, option);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  useEffect(() => {
    if (time.timeFrom || time.timeTo) {
      setActive(true);
    }
    onSearch(time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTime({
      timeFrom: defaultTimeFrom,
      timeTo: defaultTimeTo,
    });
    setActive(!!defaultTimeFrom || !!defaultTimeTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTimeFrom, defaultTimeTo]);

  return (
    <FilterBox title={title} active={active}>
      <Stack
        gap={1}
        sx={{
          border: "1px dashed",
          borderColor: "divider",
          p: 1,
          borderRadius: "10px",
        }}
      >
        {showOptions && (
          <TimeOptions grids={grids} option={option} setOption={setOption} />
        )}
        <DateInput
          direction="column"
          label="Từ ngày"
          value={time.timeFrom}
          onChange={(value) => handleTimeChange({ key: "timeFrom", value })}
        />
        <DateInput
          direction="column"
          label="Đến ngày"
          value={time.timeTo}
          onChange={(value) => handleTimeChange({ key: "timeTo", value })}
        />
        <Button
          onClick={() => handleFilterTime(time)}
          size="small"
          variant="contained"
          fullWidth
          sx={{
            boxShadow: "none",
            color: "common.white",
            textTransform: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          Áp dụng
        </Button>
      </Stack>
    </FilterBox>
  );
}

export default FilterTimeFromTo;
